// Cash-drawer bridge — the browser equivalent of Android's openCashDrawerNew() (StarXpand
// DrawerBuilder.actionOpen). A browser can't kick a physical drawer directly, so we go through a
// local print bridge. QZ Tray is the primary driver: a small local agent (Windows/macOS/Linux, every
// major browser) that the page reaches over a localhost WebSocket and that forwards a raw drawer-kick
// command to the receipt printer the drawer is wired to (Star TSP-series by default; overridable for
// Epson/generic ESC/POS). Verified end-to-end on a Star TSP100III LAN printer (drawer = Gobbler).
//
// The drawer is fired best-effort and NON-BLOCKING: a sale must never fail because the drawer/bridge
// is missing. All I/O is isolated behind an injectable loader so the pure bits (kick bytes, config
// resolution) are unit-testable without QZ installed.
//
// PRODUCTION SILENCE: unsigned QZ connections show a one-time "Allow / Remember" prompt per machine.
// For fully silent operation, install a signed QZ certificate (ops task) — the code below runs
// unsigned/anonymous, which is enough to function.

import { configureSigning } from "./qzSigning";

// Drawer-kick command as a HEX string (sent via QZ raw 'hex' format). Default is the sequence proven
// on the Star TSP100III: the Star BEL (0x07) drawer code + a 0x0A job terminator that makes the
// raster printer actually flush and fire. A plain ESC/POS "ESC p" kick is SILENTLY IGNORED by a
// raster TSP100 (no terminator ⇒ buffered forever, never drives the coil) — which is why BOTH the BEL
// code and the terminator are required. Overridable per lane for other hardware
// (generic ESC/POS printer ≈ "1B70001947FA0A").
export const DEFAULT_KICK_HEX = "1B7007F7F0D00A";

function normHex(v) {
  return String(v == null ? "" : v).replace(/[^0-9a-fA-F]/g, "").toUpperCase();
}

// Resolve drawer config from the register settings' raw row (shape isn't pinned live, so read
// defensively). Defaults mirror Android: drawer ENABLED, connector pin 2, printer = the OS default.
export function resolveDrawerConfig(settings) {
  const row = (settings && settings.raw) || {};
  const pick = (...ks) => {
    for (const k of ks) if (row[k] != null && row[k] !== "") return row[k];
    return undefined;
  };
  const enabledRaw = pick("cash_drawer_enabled", "cashDrawerEnabled", "open_cash_drawer", "cash_drawer");
  const cfg = {
    enabled: enabledRaw == null ? true : String(enabledRaw) === "1" || enabledRaw === true,
    printerName: String(pick("cash_drawer_printer", "drawer_printer", "printer_name", "receipt_printer") || "").trim(),
    // Network (LAN) printer: kick is sent raw to host:port (9100). Takes priority over printerName.
    host: String(pick("cash_drawer_host", "drawer_host", "printer_ip", "star_printer_ip") || "").trim(),
    port: Number(pick("cash_drawer_port", "drawer_port", "printer_port")) || 9100,
    hex: normHex(pick("cash_drawer_hex", "drawer_hex")) || DEFAULT_KICK_HEX,
  };
  // Per-lane override without a settings backend: set once from the browser DevTools console, e.g.
  //   localStorage.setItem('qz_drawer_host','192.168.1.118')   // network printer IP (this fleet)
  //   localStorage.setItem('qz_drawer_printer','Star TSP143')  // OR the exact Windows printer name
  //   localStorage.setItem('qz_drawer_hex','1B70001947FA0A')   // custom kick for non-Star hardware
  //   localStorage.setItem('qz_drawer_off','1')                // disable on this lane
  try {
    if (typeof localStorage !== "undefined") {
      const lp = localStorage.getItem("qz_drawer_printer");
      if (lp) cfg.printerName = lp.trim();
      const lhost = localStorage.getItem("qz_drawer_host");
      if (lhost) cfg.host = lhost.trim();
      const lport = localStorage.getItem("qz_drawer_port");
      if (lport) cfg.port = Number(lport) || cfg.port;
      const lhex = localStorage.getItem("qz_drawer_hex");
      if (lhex) cfg.hex = normHex(lhex) || cfg.hex;
      if (localStorage.getItem("qz_drawer_off") === "1") cfg.enabled = false;
    }
  } catch (e) {
    /* localStorage unavailable — ignore */
  }
  return cfg;
}

// Lazy, cached, failure-tolerant QZ loader. import() is code-split; if the package is absent it
// resolves to null (driver simply unavailable) instead of crashing the checkout.
let qzPromise = null;
export function loadQzDefault() {
  if (!qzPromise) qzPromise = import("qz-tray").then((m) => (m && m.default) || m).catch(() => null);
  return qzPromise;
}

let securityConfigured = false;
export async function qzConnect(qz) {
  if (qz.websocket.isActive && qz.websocket.isActive()) return;
  if (!securityConfigured) {
    // Sign requests with the embedded fleet cert → QZ trusts us (no "untrusted website" prompt) once
    // override.crt is installed on the lane. Falls back to unsigned/anonymous (prompts) if unavailable.
    configureSigning(qz);
    securityConfigured = true;
  }
  await qz.websocket.connect({ retries: 1, delay: 1 });
}

// The drawer plugs into the receipt printer's DK port, so the kick MUST go to that printer. The
// Windows default is frequently "Microsoft Print to PDF"/XPS (a virtual printer that silently eats
// the pulse), so blindly using getDefault() is unreliable. Match a real thermal/receipt printer by
// name (Star/TSP/Epson/…) and only fall back to the OS default when nothing matches.
export function looksLikeReceiptPrinter(name) {
  return /star|tsp|\bmcp\b|epson|\btm-?\d|receipt|thermal|\bpos\b|gobbler|snbc|bixolon|citizen/i.test(String(name || ""));
}

// Virtual "printers" that silently swallow the kick into a file/nowhere. NEVER target these — doing so
// just spawns junk PDFs (e.g. "QZ Tray Raw Print (15).pdf") and the drawer never fires.
export function isVirtualPrinter(name) {
  return /pdf|xps|onenote|fax|document writer|microsoft print|send to/i.test(String(name || ""));
}

async function resolvePrinter(cfg, qz) {
  if (cfg.printerName) return { name: cfg.printerName, how: "configured" };
  try {
    const found = await qz.printers.find();
    const list = Array.isArray(found) ? found : found ? [found] : [];
    const receipt = list.find(looksLikeReceiptPrinter);
    if (receipt) return { name: receipt, how: "auto" };
  } catch (e) {
    /* enumeration failed — fall back to the OS default */
  }
  const def = await qz.printers.getDefault();
  if (!def) return { name: "", how: "none" };
  // A network Star printer isn't a Windows printer, so the OS default is usually a PDF/virtual printer.
  // Refuse it — the caller must set the IP (setHost) or a real printer name (setPrinter).
  if (isVirtualPrinter(def)) return { name: "", how: "virtual" };
  return { name: def, how: "default" };
}

async function driverQz(cfg, qz) {
  if (!qz || !qz.websocket) return { ok: false, reason: "no-bridge" };
  await qzConnect(qz);
  let config, printer, how;
  if (cfg.host) {
    // LAN printer: open a raw socket straight to the printer — no Windows driver/install needed.
    printer = cfg.host + ":" + cfg.port;
    how = "network";
    config = qz.configs.create({ host: cfg.host, port: cfg.port }, { encoding: "ISO-8859-1" });
  } else {
    const r = await resolvePrinter(cfg, qz);
    if (!r.name) return { ok: false, reason: r.how === "virtual" ? "virtual-only" : "no-printer" };
    printer = r.name;
    how = r.how;
    config = qz.configs.create(r.name, { encoding: "ISO-8859-1" });
  }
  // Raw HEX element → QZ decodes the hex to bytes and sends verbatim (Star BEL kick + terminator).
  await qz.print(config, [{ type: "raw", format: "hex", data: cfg.hex }]);
  return { ok: true, driver: "qz", printer, how, hex: cfg.hex };
}

/**
 * Open the cash drawer. Best-effort; resolves to a result object, never throws.
 * @param {object} settings register settings (may be null)
 * @param {object} [deps] { loadQz } — injected for tests
 * @returns {Promise<{ok:boolean, driver?:string, printer?:string, how?:string, hex?:string, reason?:string, error?:string}>}
 */
export async function openCashDrawer(settings, deps = {}) {
  const cfg = resolveDrawerConfig(settings);
  if (!cfg.enabled) return { ok: false, reason: "disabled" };
  const loadQz = deps.loadQz || loadQzDefault;
  try {
    const qz = await loadQz();
    return await driverQz(cfg, qz);
  } catch (e) {
    return { ok: false, reason: "error", error: e && e.message ? e.message : String(e) };
  }
}

// Fire-and-forget wrapper for UI call sites: kicks the drawer without blocking the sale. Logs every
// outcome under [cash-drawer] so a lane's printer/permission state is visible in DevTools.
export function kickDrawer(settings) {
  openCashDrawer(settings)
    .then((r) => {
      if (r.ok) {
        if (/pdf|xps|onenote|fax|document writer/i.test(String(r.printer))) {
          // eslint-disable-next-line no-console
          console.warn(
            "[cash-drawer] sent to a VIRTUAL printer (" + r.printer + ") — a physical drawer won't open.",
            "\n→ Pin the real Star printer: __cashDrawer.setPrinter('<exact Star printer name>')  (list them with __cashDrawer.printers())"
          );
        } else {
          // eslint-disable-next-line no-console
          console.info("[cash-drawer] kicked → printer:", r.printer, "(" + r.how + ")");
        }
      } else if (r.reason !== "disabled") {
        // eslint-disable-next-line no-console
        console.warn(
          "[cash-drawer] NOT opened (" + r.reason + ").",
          r.error || "",
          r.reason === "virtual-only"
            ? "\n→ It defaulted to a PDF/virtual printer. Network printer? run  __cashDrawer.setHost('192.168.1.118')"
            : "\n→ Network printer? run  __cashDrawer.setHost('<printer ip>')   USB? __cashDrawer.printers() then __cashDrawer.setPrinter('<name>')",
          "\n→ QZ Tray running + request Allowed (tick 'Remember this decision')?"
        );
      }
    })
    .catch(() => {});
}

// DevTools diagnostics (tiny, safe). From the register page console:
//   __cashDrawer.setHost('192.168.1.118')  → network printer IP (this fleet's setup)
//   __cashDrawer.printers()                → list every printer QZ can see
//   __cashDrawer.setPrinter('name')        → OR pin to an exact Windows printer name
//   __cashDrawer.setCommand('1B..0A')      → custom kick hex for non-Star hardware
//   __cashDrawer.test()                    → fire a kick now and log the full result object
if (typeof window !== "undefined") {
  window.__cashDrawer = {
    test: (settings) =>
      openCashDrawer(settings).then((r) => {
        // eslint-disable-next-line no-console
        console.log("[cash-drawer] test →", r);
        return r;
      }),
    printers: async () => {
      const qz = await loadQzDefault();
      if (!qz) {
        // eslint-disable-next-line no-console
        console.warn("[cash-drawer] QZ Tray not reachable (is the QZ Tray app running?)");
        return null;
      }
      await qzConnect(qz);
      const list = await qz.printers.find();
      // eslint-disable-next-line no-console
      console.log("[cash-drawer] printers →", list);
      return list;
    },
    setPrinter: (name) => {
      try {
        localStorage.setItem("qz_drawer_printer", name);
      } catch (e) {
        /* ignore */
      }
      return "drawer printer set to: " + name;
    },
    setCommand: (hex) => {
      try {
        localStorage.setItem("qz_drawer_hex", String(hex));
      } catch (e) {
        /* ignore */
      }
      return "drawer kick hex set to: " + hex;
    },
    // Network (LAN) Star printer: send the kick straight to its IP — no Windows driver needed.
    setHost: (ip, port) => {
      try {
        localStorage.setItem("qz_drawer_host", String(ip));
        if (port) localStorage.setItem("qz_drawer_port", String(port));
      } catch (e) {
        /* ignore */
      }
      return "drawer host set to: " + ip + ":" + (port || 9100);
    },
  };
}
